/**
 * Plain-English health copy for latest-period statement ratios (ADR-0035).
 * Missing inputs yield a null tone — they are not scored as "neutral" (R2b).
 */
import { debtToEquity } from './statement-ratios';

export type HealthTone = 'good' | 'bad' | 'neutral';

export interface StatementHealth {
  readonly hint: string;
  readonly tone: HealthTone | null;
}

const MISSING: StatementHealth = { hint: 'Missing is not zero.', tone: null };

/** D/E < 1 owns more than it owes; < 2 moderate; else high (ADR-0035). */
export const DEBT_EQUITY_HEALTHY_MAX = 1;
export const DEBT_EQUITY_MODERATE_MAX = 2;

export function debtEquityHealth(de: number | null): StatementHealth {
  if (de === null || !Number.isFinite(de)) return MISSING;
  if (de < DEBT_EQUITY_HEALTHY_MAX) {
    return { hint: 'Owns more than it owes — healthy', tone: 'good' };
  }
  if (de < DEBT_EQUITY_MODERATE_MAX) return { hint: 'Moderate leverage', tone: 'neutral' };
  return { hint: 'High leverage — more debt than equity', tone: 'bad' };
}

const NEGATIVE_EQUITY: StatementHealth = {
  hint: 'Liabilities exceed assets — negative equity',
  tone: 'bad',
};

const ZERO_EQUITY: StatementHealth = {
  hint: 'Zero book equity — D/E is undefined',
  tone: 'bad',
};

/** Negative equity is a real observation, not a missing D/E (ADR-0022). */
export function debtEquityHealthFromLegs(
  debt: number | null,
  equity: number | null,
): StatementHealth {
  if (equity !== null && Number.isFinite(equity) && equity < 0) return NEGATIVE_EQUITY;
  if (equity !== null && Number.isFinite(equity) && equity === 0 && debt !== null) {
    return Number.isFinite(debt) ? ZERO_EQUITY : MISSING;
  }
  return debtEquityHealth(debtToEquity(debt, equity));
}

/** Current ratio ≥ 1.5 comfortable cover; ≥ 1 covering; else strained (ADR-0035). */
export const CURRENT_RATIO_COMFORTABLE_MIN = 1.5;
export const CURRENT_RATIO_COVERING_MIN = 1;

export function currentRatioHealth(cr: number | null): StatementHealth {
  if (cr === null || !Number.isFinite(cr)) return MISSING;
  if (cr >= CURRENT_RATIO_COMFORTABLE_MIN) {
    return { hint: 'Can easily cover short-term bills', tone: 'good' };
  }
  if (cr >= CURRENT_RATIO_COVERING_MIN) {
    return { hint: 'Can cover short-term bills', tone: 'good' };
  }
  return { hint: 'May struggle to pay near-term bills', tone: 'bad' };
}

/** Net debt ≤ 0 is net cash (ADR-0035). */
export const NET_CASH_MAX = 0;

export function netDebtHealth(netDebt: number | null): StatementHealth {
  if (netDebt === null || !Number.isFinite(netDebt)) return MISSING;
  if (netDebt <= NET_CASH_MAX) return { hint: 'More cash than debt — strong', tone: 'good' };
  return { hint: 'Total debt minus cash on hand', tone: 'neutral' };
}
